import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { NumericFormat } from 'react-number-format'
import { useDispatch, useSelector } from 'react-redux'
import { TextField } from '@mui/material'
import PropTypes from 'prop-types'
import { useDebouncedCallback } from 'use-debounce'

import { selectLocale } from '@/app/slices/appSlice'
import { addItemToUpdateQueue } from '@/app/slices/orderItemsSlice'
import { selectCurrencyCode } from '@/app/slices/organisationSlice'
import TbxTooltip from '@/common/components/TbxTooltip/TbxTooltip'
import { getCurrencyFormat } from '@/common/utils'

const classes = {
    formControl_small: {
        width: '100px',
    },
}

const QuantitySecProcess = ({
    isPartFromLibrary = false,
    onInputChange,
    orderItemId,
    process,
    showLabel = true,
    sx = null,
}) => {
    const { t } = useTranslation()
    const dispatch = useDispatch()

    const locale = useSelector(selectLocale)
    const currencyCode = useSelector(selectCurrencyCode)

    const [itemQuantity, setItemQuantity] = useState(process.input || 1)

    const [quantityError, setQuantityError] = useState(!process?.input)

    const { $type, ...rest } = process

    const currencyFormat = getCurrencyFormat(currencyCode, locale)

    const debounceOnInputChange = useDebouncedCallback(onInputChange, 1000)

    const handleInputChange = async (values) => {
        debounceOnInputChange.cancel()
        dispatch(addItemToUpdateQueue(orderItemId))
        const { floatValue } = values

        setQuantityError(!floatValue || isNaN(floatValue) || floatValue === 0)
        setItemQuantity(floatValue)

        if (!isNaN(floatValue) && floatValue !== 0) {
            debounceOnInputChange({ ...rest, isEnabled: true, input: floatValue })
        }
    }

    return (
        <TbxTooltip
            title={isPartFromLibrary ? t('Cannot edit parts from library') : ''}
            arrow
        >
            <NumericFormat
                allowNegative={true}
                customInput={TextField}
                decimalSeparator={currencyFormat.decimal}
                disabled={isPartFromLibrary}
                error={quantityError}
                label={showLabel ? t('Quantity') : null}
                size="small"
                slotProps={{
                    inputLabel: {
                        shrink: true,
                    },
                    htmlInput: {
                        style: { textAlign: 'right', paddingBlock: '6px', paddingInline: '12px' },
                        'data-testid': 'order-item-secondary-process-input',
                    },
                    input: {
                        style: { fontSize: '14px' },
                    },
                }}
                sx={[classes.formControl_small, ...(Array.isArray(sx) ? sx : [sx])]}
                thousandSeparator={currencyFormat.group}
                value={itemQuantity}
                variant="outlined"
                onValueChange={handleInputChange}
            />
        </TbxTooltip>
    )
}

QuantitySecProcess.propTypes = {
    isPartFromLibrary: PropTypes.bool,
    orderItemId: PropTypes.string,
    process: PropTypes.object,
    showLabel: PropTypes.bool,
    sx: PropTypes.oneOfType([
        PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.func, PropTypes.object, PropTypes.bool])),
        PropTypes.func,
        PropTypes.object,
    ]),
    onInputChange: PropTypes.func,
}

export default QuantitySecProcess
